import type {
  ContentVersionBlob,
  EditorResource,
  ReadContentVersionsDocument,
} from '../../v3/contentVersions/types'
import type { ContentVersion } from './types'

/**
 * Flattens the JSON:API document into view models, resolving each version's author
 * from the `included` users. The API returns versions newest first; that order is
 * preserved.
 */
export const flattenContentVersions = (
  document: ReadContentVersionsDocument
): ContentVersion[] => {
  const editorsById = new Map<string, EditorResource>()
  for (const resource of document.included ?? []) {
    if (resource.type === 'user') editorsById.set(resource.id, resource)
  }

  return document.data.map((resource) => {
    const editorId = resource.relationships.editor.data?.id
    const editor = editorId ? editorsById.get(editorId) : undefined

    return {
      id: resource.id,
      label: resource.attributes.label,
      createdAt: resource.attributes.created_at,
      authorName: editor?.attributes.full_name ?? 'Unknown',
      activityId: resource.attributes.activity_id,
      annotationsCount: resource.attributes.annotations_count ?? 0,
      unresolvedAnnotationsCount:
        resource.attributes.unresolved_annotations_count ?? 0,
      snapshotUrl: resource.attributes.snapshot_url ?? null,
      // The generated schema types the blob loosely ({ [key]: unknown }); the
      // capture blob contract (schema_version 1) is the hand-written type.
      blob: resource.attributes.content_blob as ContentVersionBlob,
    }
  })
}
